import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { useEffect } from 'react';
import KihubAdminStore from '../stores/KihubAdminStore';
import type { KihubDatabaseStore } from '../stores/KihubAdminStore';

const ProviderSelection = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    const handleChangeProvider = async (providerId: string) => {
        const selectedProvider = store.providerList.find((p: any) => p.id === providerId);
        const providerName = selectedProvider?.name || 'Unbekannter Provider';

        const confirmResult = await sdk.getDialogConfirm({
            title: 'AI-Provider wechseln',
            message: `Möchten Sie wirklich zum Provider "${providerName}" wechseln? Ihre aktuellen API-Einstellungen gehen dabei verloren.`,
            onConfirm: async () => {
                const result = await store.changeProvider(providerId);
                if (result.success) {
                    return { success: true, status: 200 };
                } else {
                    return {
                        success: false,
                        status: 400,
                        error: 'Fehler beim Wechseln des Providers',
                    };
                }
            },
        });

        if (confirmResult.confirmed) {
            sdk.showSuccessToast('AI-Provider erfolgreich geändert');
            store.refresh();
        } else if (confirmResult.error) {
            sdk.showErrorToast('Fehler beim Ändern des AI-Providers');
        }
    };
    const providerOptions = store.providerList?.map((provider: any) => ({
        label: provider.name,
        value: provider.id,
    }));

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <label
                style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold',
                    color: '#333',
                }}
            >
                AI-Provider auswählen:
            </label>
            <Dropdown
                value={store.provider?.id || ''}
                options={providerOptions}
                onChange={e => handleChangeProvider(e.value)}
                placeholder="Provider auswählen"
                style={{
                    width: '100%',
                    borderRadius: '6px',
                }}
                disabled={store.providerList.length === 0}
            />
        </div>
    );
});

const AISettings = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    if (!store) return null;

    const handleChangeApiKey = async () => {
        const providerName =
            store.providerList.find((p: any) => p.id === store.provider?.id)?.name || 'AI-Provider';

        const result = await sdk.getDialogStringInput({
            title: 'API Key eingeben',
            message: `Bitte geben Sie Ihren ${providerName} API Key ein`,
            initialValue: store.openai_api_key || '',
            label: 'API Key',
            onConfirm: async ({ input }: { input?: string }) => store.saveApiKey(input),
        });

        if (result.confirmed) {
            sdk.showSuccessToast('API-Key erfolgreich hinzugefügt');
        } else if (result.error) {
            sdk.showErrorToast('API-Key ungültig');
        }
    };

    return (
        <Card
            title="AI-Provider Konfiguration"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="p-fluid">
                <ProviderSelection
                    store={store}
                    sdk={sdk}
                />

                {!store.hasProvider ? (
                    <Message
                        severity="info"
                        text="Bitte wählen Sie zuerst einen AI-Provider aus"
                        style={{ marginBottom: '1.5rem' }}
                    />
                ) : store.hasApiKey ? (
                    <div>
                        <Message
                            severity="success"
                            text="AI-Provider API-Schlüssel ist konfiguriert"
                            style={{ marginBottom: '1.5rem' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                label="API-Schlüssel ändern"
                                icon="pi pi-pencil"
                                onClick={handleChangeApiKey}
                                className="p-button-outlined"
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    borderRadius: '6px',
                                }}
                            />
                        </div>
                    </div>
                ) : store.hasProvider && store.providerOptions.apiKeyNeeded ? (
                    <div>
                        <Message
                            severity="warn"
                            text="Kein AI-Provider API-Schlüssel konfiguriert - API-Schlüssel ist erforderlich"
                            style={{ marginBottom: '1.5rem' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                label="API-Schlüssel hinzufügen"
                                icon="pi pi-plus"
                                onClick={handleChangeApiKey}
                                className="p-button-primary"
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    borderRadius: '6px',
                                }}
                            />
                            <small
                                style={{
                                    color: '#666',
                                    fontSize: '0.85rem',
                                    textAlign: 'center',
                                }}
                            >
                                Sie benötigen einen AI-Provider API-Schlüssel, um die KI-Funktionen
                                zu nutzen
                            </small>
                        </div>
                    </div>
                ) : null}
            </div>
        </Card>
    );
});

const KihubDatabaseComponent = observer(
    ({
        store,
        sdk,
        kihubDatabase,
    }: {
        store: any;
        sdk: ApplicationSdk;
        kihubDatabase: KihubDatabaseStore;
    }) => {
        const isEnabled = kihubDatabase.enabled;

        const handleActivateDatabase = async () => {
            const activationResult = await sdk.getDialogWait({
                title: 'Wissensdatenbank aktivieren',
                message: 'Die Wissensdatenbank wird aktiviert. Dies kann einen Moment dauern.',
                onConfirm: async () => kihubDatabase.activate(store),
            });

            if (activationResult.confirmed) {
                store.refresh();
                sdk.showSuccessToast('Wissensdatenbank erfolgreich aktiviert.');
            } else if (activationResult.error) {
                sdk.showErrorToast(activationResult.error);
            }
        };

        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Tag
                            value={isEnabled ? 'Aktiv' : 'Inaktiv'}
                            severity={isEnabled ? 'success' : 'danger'}
                            icon={isEnabled ? 'pi pi-check' : 'pi pi-times'}
                        />
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Name:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: isEnabled ? '#333' : '#666',
                            }}
                        >
                            {kihubDatabase.label || 'Kein Name'}
                        </p>
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Beschreibung:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: isEnabled ? '#333' : '#666',
                            }}
                        >
                            {kihubDatabase.description || 'Keine Beschreibung'}
                        </p>
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Vector Store ID:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: isEnabled ? '#666' : '#999',
                                fontSize: '0.85rem',
                                fontFamily: 'monospace',
                            }}
                        >
                            {kihubDatabase.vectorStoreId || 'Nicht verfügbar'}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        {!isEnabled ? (
                            <Button
                                label="Aktivieren"
                                icon="pi pi-power-off"
                                onClick={handleActivateDatabase}
                                className="p-button-success"
                                // loading={kihubDatabase.loading}
                                style={{ padding: '0.5rem 1rem' }}
                            />
                        ) : (
                            <Button
                                label="Bearbeiten"
                                icon="pi pi-pencil"
                                onClick={() => kihubDatabase.edit(sdk, store)}
                                className="p-button-outlined"
                                style={{ padding: '0.5rem 1rem' }}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    }
);

const KihubDatabasesOverview = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    const kihubDatabases = store.kihubDatabases || [];

    return (
        <Card
            title="Wissensdatenbanken"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="p-fluid">
                {!kihubDatabases || kihubDatabases.length === 0 ? (
                    <Message
                        severity="info"
                        text="Keine Wissensdatenbanken verfügbar"
                        style={{ marginBottom: '1rem' }}
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {kihubDatabases.map((kihubDb: any) => {
                            return (
                                <KihubDatabaseComponent
                                    key={kihubDb.id}
                                    store={store}
                                    kihubDatabase={kihubDb}
                                    sdk={sdk}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </Card>
    );
});

const KihubHookSettings = observer(({ store, sdk }: { store: any; sdk: ApplicationSdk }) => {
    const webhookSettings = store.settings || {};

    const handleEditWebhooks = async () => {
        const webhookEditResponse = await sdk.getDialogMultiInput({
            title: 'Webhook-Einstellungen bearbeiten',
            fields: [
                {
                    label: 'Webhook-URL (Modul Anlernen)',
                    key: 'moduleLearn',
                    type: 'text',
                    placeholder: 'https://example.com/webhook/module-learn',
                },
                {
                    label: 'Webhook-URL (Modul Löschen)',
                    key: 'moduleDelete',
                    type: 'text',
                    placeholder: 'https://example.com/webhook/module-delete',
                },
                {
                    label: 'Webhook-URL (Modul-Eintrag löschen)',
                    key: 'moduleEntryDelete',
                    type: 'text',
                    placeholder: 'https://example.com/webhook/entry-delete',
                },
            ],
            initialValue: {
                moduleLearn: webhookSettings.webhookModuleLearn || '',
                moduleDelete: webhookSettings.webhookModuleDelete || '',
                moduleEntryDelete: webhookSettings.webhookModuleEntryDelete || '',
            },
            message:
                'Hier können Sie die Webhook-URLs für die verschiedenen KI-Hub Ereignisse konfigurieren.',
            onConfirm: async (values: any) =>
                store.saveWebhookSettings(
                    values.moduleLearn,
                    values.moduleDelete,
                    values.moduleEntryDelete
                ),
        });

        if (webhookEditResponse.confirmed) {
            store.refresh();
            sdk.showSuccessToast('Webhook-Einstellungen erfolgreich aktualisiert.');
        } else if (webhookEditResponse.error) {
            sdk.showErrorToast('Fehler beim Aktualisieren der Webhook-Einstellungen');
        }
    };

    return (
        <Card
            title="Webhook-Einstellungen"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="p-fluid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Webhook-URL (Modul Anlernen):
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: '#333',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all',
                            }}
                        >
                            {webhookSettings.webhookModuleLearn || 'Nicht konfiguriert'}
                        </p>
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Webhook-URL (Modul Löschen):
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: '#333',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all',
                            }}
                        >
                            {webhookSettings.webhookModuleDelete || 'Nicht konfiguriert'}
                        </p>
                    </div>
                    <div>
                        <label
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                        >
                            Webhook-URL (Modul-Eintrag löschen):
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                color: '#333',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all',
                            }}
                        >
                            {webhookSettings.webhookModuleEntryDelete || 'Nicht konfiguriert'}
                        </p>
                    </div>

                    {/* Action Button */}
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button
                            label="Bearbeiten"
                            icon="pi pi-pencil"
                            onClick={handleEditWebhooks}
                            className="p-button-outlined"
                            style={{ padding: '0.5rem 1rem' }}
                        />
                    </div>
                </div>
            </div>
        </Card>
    );
});

const KihubAdminComponent = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(KihubAdminStore);
    }, [sdk]);

    const store = sdk.currentStore;

    if (!store?.isKiHub || store.isLoading) {
        return (
            <div
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '2rem',
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ProgressSpinner style={{ display: 'block', margin: '2rem auto' }} />
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '2rem',
                minHeight: '100vh',
            }}
        >
            {store.error && (
                <Message
                    severity="error"
                    text={store.error}
                    style={{ marginBottom: '1.5rem' }}
                />
            )}

            <div className="p-fluid">
                <AISettings
                    store={store}
                    sdk={sdk}
                />
                {(store.hasApiKey || store.providerOptions?.apiKeyNeeded === false) && (
                    <>
                        <KihubDatabasesOverview
                            store={store}
                            sdk={sdk}
                        />
                        {store.providerOptions?.webhook === true ? (
                            <KihubHookSettings
                                store={store}
                                sdk={sdk}
                            />
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
});

export default KihubAdminComponent;
